import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import CategorizeRow from "../components/CategorizeRow";

const CONCURRENCY = 3;

export default function BulkCategorize() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const touched = useRef(new Set());

  const patch = (id, fields) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...fields } : r)));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/manage/uncategorized");
      if (cancelled) return;
      setRows(data.map((p) => ({ ...p, choice: null, suggestion: null, suggesting: true })));
      const queue = [...data];
      const worker = async () => {
        while (queue.length && !cancelled) {
          const p = queue.shift();
          let slug = null;
          try { slug = (await api.post(`/manage/photos/${p.id}/suggest-category`)).data.slug; } catch { /* no suggestion */ }
          if (cancelled) return;
          setRows((rs) => rs.map((r) => (r.id !== p.id ? r : {
            ...r, suggestion: slug, suggesting: false,
            choice: touched.current.has(p.id) ? r.choice : slug,
          })));
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    })().catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    return () => { cancelled = true; };
  }, []);

  const onPick = (id, slug) => { touched.current.add(id); patch(id, { choice: slug }); };
  const ready = (rows || []).filter((r) => r.choice);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/manage/bulk-category", {
        items: ready.map((r) => ({ id: r.id, category: r.choice })),
      });
      const saved = new Set(ready.map((r) => r.id));
      setRows((rs) => rs.filter((r) => !saved.has(r.id)));
      toast.success(t("bc_saved", data.updated));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 sm:px-10 py-16" data-testid="bulk-categorize-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">{t("tools_eyebrow")}</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-zinc-50 font-light tracking-tight">{t("bc_title")}</h1>
      <p className="mt-4 max-w-2xl text-zinc-400 font-light text-sm sm:text-base">{t("bc_sub")}</p>

      {rows === null ? (
        <p className="mt-12 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500" data-testid="bc-loading">{t("loading")}</p>
      ) : rows.length === 0 ? (
        <p className="mt-12 text-zinc-300 font-light" data-testid="bc-empty">{t("bc_empty")}</p>
      ) : (
        <>
          <ul className="mt-12" data-testid="bc-list">
            {rows.map((r) => <CategorizeRow key={r.id} row={r} onPick={onPick} />)}
          </ul>
          <div className="sticky bottom-0 mt-8 -mx-6 sm:-mx-10 px-6 sm:px-10 py-4 border-t border-zinc-800 bg-zinc-950/90 [backdrop-filter:blur(10px)] flex justify-end">
            <button
              onClick={save}
              disabled={saving || ready.length === 0}
              className="bg-sienna text-white px-6 py-3 text-sm font-light hover:bg-sienna-400 disabled:opacity-50 transition-colors"
              data-testid="bc-save-button"
            >
              {saving ? t("loading") : t("bc_save", ready.length)}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
